'use client'

import { useState, useEffect } from 'react'
import { Link2Icon, SaveIcon, UnlinkIcon } from 'lucide-react'
import { useEditorContext } from '@/components/ui/editor'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const Link = () => {
  const { editor, sourceMode } = useEditorContext()
  const [url, setUrl] = useState('')
  const [displayText, setDisplayText] = useState('')
  const [openInNewTab, setOpenInNewTab] = useState(false)
  const [open, setOpen] = useState(false)
  const [hasLink, setHasLink] = useState(false)

  useEffect(() => {
    if (!editor) {
      return
    }

    const updateState = () => {
      const attrs = editor.getAttributes('link')
      setUrl(attrs.href || '')
      setOpenInNewTab(attrs.target === '_blank')
      setHasLink(editor.isActive('link'))

      if (editor.isActive('link')) {
        const linkText = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, ' ')
        setDisplayText(linkText || '')
      } else {
        setDisplayText('')
      }
    }

    updateState()

    editor.on('selectionUpdate', updateState)
    editor.on('transaction', updateState)

    return () => {
      editor.off('selectionUpdate', updateState)
      editor.off('transaction', updateState)
    }
  }, [editor])

  useEffect(() => {
    if (!editor || !open) {
      return
    }

    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to, ' ')

    setDisplayText(selectedText)
  }, [open, editor])

  const applyLink = () => {
    if (!url.trim()) {
      return
    }

    const { from, to } = editor.state.selection
    const hasSelection = from !== to
    const textToInsert = displayText.trim() || url.trim()

    editor.chain().focus()

    if (hasSelection) {
      editor.commands.insertContentAt({ from, to }, textToInsert)
      editor.commands.setTextSelection({
        from,
        to: from + textToInsert.length
      })
    } else {
      editor.commands.insertContentAt(from, textToInsert)
      editor.commands.setTextSelection({
        from,
        to: from + textToInsert.length
      })
    }

    editor.commands.setLink({
      href: url.trim(),
      target: openInNewTab ? '_blank' : null
    })

    editor.commands.focus()

    setOpen(false)
  }

  const removeLink = () => {
    editor.chain().focus().unsetLink().run()
    setOpen(false)
  }

  return (
    <Tooltip>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant={editor.isActive('link') ? 'default' : 'ghost'}
              title="Add link"
              onClick={() => setOpen(!open)}
              disabled={sourceMode}
              tabIndex={-1}>
              <Link2Icon />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>
          <p>Add link</p>
        </TooltipContent>

        <PopoverContent className="w-72 space-y-5">
          <div className="space-y-2">
            <Input
              type="url"
              placeholder="https://example.com"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  applyLink()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setOpen(false)
                }
              }}
              autoFocus
            />
            <Input
              type="text"
              placeholder="Display Text (optional)"
              value={displayText}
              onChange={e => setDisplayText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  applyLink()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setOpen(false)
                }
              }}
            />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                id="openInNewTab"
                checked={openInNewTab}
                onCheckedChange={checked => setOpenInNewTab(!!checked)}
              />
              Open in new tab
            </label>
          </div>
          <div className="flex justify-between gap-2">
            <Button type="button" variant="destructive" size="sm" onClick={removeLink} disabled={!hasLink}>
              <UnlinkIcon />
              Remove
            </Button>
            <Button type="button" size="sm" onClick={applyLink} disabled={!url.trim()}>
              <SaveIcon />
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </Tooltip>
  )
}

export default Link
